/**
 * Abi's page guides (prototype v45 `GUIDES`; steps and sentences describing data or features that don't
 * exist yet are left out, Ian 2026-11-01, listed in docs/design/ux-issues.md): one per page, run the first time the
 * page is opened and again from its "Page guide" button. Step one is centred over a veil; every
 * later step points at `sel`, the first visible match of a comma-separated list (a step whose
 * anchor isn't on the page is skipped). `wait` steps advance when the user clicks a match of it;
 * `tryit` adds the "Try it" tag.
 *
 * Not in the copy config, as in the prototype (its COPY-CONFIG.md: keyed by page, not by element).
 */
export type GuideStep = { t: string; sel?: string; tryit?: boolean; wait?: string };

/** The pages with a guide, by `NAV` key (Sharpen and Settings have none). */
export type GuideKey =
  | "home"
  | "cashflow"
  | "budget"
  | "assets"
  | "debt"
  | "networth"
  | "retirement"
  | "priorities"
  | "decisions"
  | "actions"
  | "achievements"
  | "updatedata";

const page = (testId: string, sel: string) => `[data-testid="page-${testId}"] ${sel}`;

export const GUIDES: Record<GuideKey, GuideStep[]> = {
  home: [
    {
      t: "Hi, I'm Abi. Your plan is built — which already puts you ahead of most people, who never get one made at all.",
    },
    {
      sel: "#app .tabwrap, #app .mtabs",
      t: "Everything lives up here. I've put Actions second, right after Home, because that's the page that tells you what to actually do.",
    },
    {
      t: "I'll pop up the first time you open each page, show you around it, and then get out of your way. Go have a look at Cash Flow.",
    },
  ],

  cashflow: [
    {
      sel: page("cashflow", ".cfvtabs"),
      t: "Each view answers a different question — have a click through them.",
    },
    {
      sel: "#cf-dates",
      t: "And you can change the window here.",
    },
  ],

  budget: [
    {
      t: "Your budget starts as whatever you already spend, so you have one whether you set it up or not.",
    },
  ],

  assets: [
    {
      t: "Everything you own that's out working for you.",
    },
    {
      sel: "#hd-ast",
      t: "What you hold now, and where the plan takes it by the time you stop working.",
    },
    {
      sel: "#ast-growth",
      t: "Your accounts stacked up and growing. Hover any year for the total across all of them.",
    },
  ],

  debt: [
    {
      t: "Everything you owe, what each payment is really buying, and what changes if you follow the plan.",
    },
    {
      sel: "#dcmp-rings",
      t: "Your current path next to mine. Where mine costs you something, I say so in plain words right underneath — I'm not going to hide it.",
    },
    {
      sel: "#dbtabs",
      t: "One debt at a time, because a mortgage and a credit card can't share a scale. Blue is where you're headed now, gold is the plan.",
    },
    {
      sel: "#debtsplit",
      t: "This is the one worth staring at: how much of each payment is interest rather than progress.",
    },
  ],

  networth: [
    { t: "What you own, what you owe, and the gap between them. That gap is your net worth." },
    {
      sel: "#nwbig",
      t: "And where it's heading. Run your pointer across it and I'll read you both lines at any year you like.",
    },
  ],

  retirement: [
    {
      t: "The one goal everything else has to fit around: the year you stop working, and what it takes to get there.",
    },
    {
      sel: "#hd-ret",
      t: "How much of it you've funded so far, where your current path lands, and where mine does.",
    },
    {
      sel: "#ff-acc",
      t: "This is the money building up between now and then. Hover a year for the total across every account.",
    },
  ],

  priorities: [
    {
      t: "The things you told me matter, in the order you ranked them. Everything I do is in service of this list.",
    },
    {
      sel: "#hd-pri",
      t: "How much of your top one is paid for today, and how many of them get fully funded on each path.",
    },
    {
      sel: "#prios",
      t: "Each one, your current path against mine.",
    },
  ],

  decisions: [
    {
      t: "Thinking about something big? Try it here first and see what it does to the rest of your plan before you commit to anything.",
    },
    {
      sel: "#hub",
      t: "Pick one, put your numbers in, press Analyze. Nothing you do in here touches your actual plan.",
    },
  ],

  actions: [
    {
      t: "The short list, in the order I'd do them. If something made it onto this page, it's worth your time.",
    },
    {
      sel: "#act-full",
      t: "Open any one to see why it's on the list.",
    },
  ],

  achievements: [
    {
      t: "This page fills in as you go. It's the honest answer to ‘has any of this actually worked?’",
    },
    {
      sel: "#achtabs",
      t: "Six measures, each one comparing where you are now with where you'd have been.",
    },
  ],

  updatedata: [
    {
      t: "Anything I got wrong, you can fix here. The plan rebuilds itself around whatever you change.",
    },
    {
      sel: page("updatedata", ".card"),
      t: "Each section opens up on its own. Change what you need to and leave the rest alone — nothing is required.",
    },
  ],
};

export function isGuideKey(key: string | undefined): key is GuideKey {
  return !!key && key in GUIDES;
}
